import { /*Map,*/ fromJS } from "immutable";
import { handleActions, createAction } from "redux-actions";
import Restful from "lib/restful";
import GaHelper from "lib/gaHelper";
import config from "lib/fr-config";
import { sleep } from "lib/commonUtil";

const UNIT_LENGTH = config.outfitList.LENGTH;
const EXP_MINUTE = 30;
const WAIT_MILLISEC = config.storeModule.waitForLoadingMilliSec;

const SET_NOW_FOLDER_HASH_AND_VALUE = "bookmark/SET_NOW_FOLDER_HASH_AND_VALUE";
const SET_OUTFIT_LIST = "bookmark/SET_OUTFIT_LIST";
const SET_FOLDER_LIST = "bookmark/SET_FOLDER_LIST";
const CHECK_IS_END = "bookmark/CHECK_IS_END";
const SET_EXPIRATION_DATE = "bookmark/SET_EXPIRATION_DATE";
const SET_IS_LOADING = "bookmark/SET_IS_LOADING";
const EXPORT_BOOKMARK_DATA = "bookmark/EXPORT_BOOKMARK_DATA";
const INIT_EXPORTED_LIST = "bookmark/INIT_EXPORTED_LIST";
const INIT_FOLDER_OBJ = "bookmark/INIT_FOLDER_OBJ";
const INIT_ALL_DATA = "bookmark/INIT_ALL_DATA";

const setNowFolderHashAndValue = createAction(SET_NOW_FOLDER_HASH_AND_VALUE);
const setOutfitList = createAction(SET_OUTFIT_LIST);
const setFolderList = createAction(SET_FOLDER_LIST);
const checkIsEnd = createAction(CHECK_IS_END);
const setExpirationDate = createAction(SET_EXPIRATION_DATE);
const setIsLoading = createAction(SET_IS_LOADING);
const exportBookmarkData = createAction(EXPORT_BOOKMARK_DATA);
const initExportedList = createAction(INIT_EXPORTED_LIST);
const initFolderObj = createAction(INIT_FOLDER_OBJ);
const initAllData = createAction(INIT_ALL_DATA);

const getBookmarkState = ({ getState }) => {
  const bookmarkState = getState().bookmark.toJS();
  return bookmarkState;
};

const getIsLoading = ({ getState }) => {
  const bookmarkState = getBookmarkState({ getState });
  const { isLoading } = bookmarkState;
  return isLoading;
};

const getLastHash = list => {
  if (list.length === 0) return undefined;
  const length = list.length;
  return list[length - 1]["outfitHash"];
};

const getTargetData = ({ getState, hash }) => {
  const bookmarkState = getBookmarkState({ getState });
  return bookmarkState.bookmarkObj[hash];
};

const checkExpDate = ({ getState, hash }) => {
  const targetData = getTargetData({ getState, hash });
  const expirationDate = targetData && targetData.expirationDate;
  if (!expirationDate) return { needUpdate: true };
  const nowDate = new Date();
  return { needUpdate: nowDate > expirationDate ? true : false };
};

const setExpDate = ({ dispatch, hash }) => {
  const expirationDate = new Date();
  expirationDate.setMinutes(expirationDate.getMinutes() + EXP_MINUTE);
  dispatch(setExpirationDate({ expirationDate, hash }));
};

const setNowFolder = ({ getState, dispatch, hash }) => {
  const bookmarkState = getBookmarkState({ getState });
  const { bookmarkFolderList } = bookmarkState;

  let name = "";

  for (let i = 0; i < bookmarkFolderList.length; i++) {
    const element = bookmarkFolderList[i];
    if (element.hash === hash) {
      name = element.name;
      break;
    }
  }

  dispatch(setNowFolderHashAndValue({ hash, name }));
};

const requestOutfitList = async ({ savedList, hash, idToken }) => {
  const length = UNIT_LENGTH;
  const lastHash = getLastHash(savedList);
  if (hash === "all") {
    const { code, res, errInfo } = await Restful.getBookmarkListAPI({
      idToken,
      lastHash,
      length
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      throw errObj;
    } else {
      const { list } = res;
      return { list };
    }
  } else {
    const { code, res, errInfo } = await Restful.getFolderBookmarkListAPI({
      idToken,
      bookmarkFolderHash: hash,
      lastHash,
      length
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      throw errObj;
    } else {
      const { list } = res;
      return { list };
    }
  }
};

const addNewList = async ({ hash, idToken, dispatch, getState }) => {
  const { savedList } = getTargetData({ getState, hash });
  const { list } = await requestOutfitList({
    savedList,
    hash,
    idToken
  });

  dispatch(setOutfitList({ hash, savedList: savedList.concat(list) }));
  dispatch(checkIsEnd({ hash, length: list.length }));
  GaHelper.setGARequestOutfitListEvent({
    type: "bookmarkOutfitList",
    pageNum: savedList.length / UNIT_LENGTH + 1
  });

  return null;
};

const getNowFolder = ({ getState }) => {
  const bookmarkState = getBookmarkState({ getState });
  const { nowBookmarkFolderHash, nowBookmarkFolderName } = bookmarkState;
  return { nowHash: nowBookmarkFolderHash, nowName: nowBookmarkFolderName };
};

const getFirstList = async ({ hash, idToken, dispatch, getState }) => {
  let isLoading = getIsLoading({ getState });

  while (isLoading) {
    await sleep(WAIT_MILLISEC);
    isLoading = getIsLoading({ getState });
  }

  dispatch(setIsLoading(true));

  setNowFolder({ getState, dispatch, hash });

  const { needUpdate } = checkExpDate({ getState, hash });
  if (needUpdate) {
    dispatch(initExportedList());
    dispatch(initFolderObj(hash));
    try {
      await addNewList({ hash, idToken, dispatch, getState });
    } catch (error) {
      dispatch(setIsLoading(false));
      throw error;
    }
  }

  const { nowHash } = getNowFolder({ getState });
  if (nowHash === hash) {
    dispatch(exportBookmarkData(hash));
  }

  if (needUpdate) {
    setExpDate({ dispatch, hash });
  }

  dispatch(setIsLoading(false));
};

const getFolderList = async ({ idToken, dispatch, getState }) => {
  const { code, res, errInfo } = await Restful.getBookmarkFolderListAPI({
    idToken
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  const { list } = res;
  dispatch(setFolderList({ list }));
};

const writeNewFolderNameToBackend = async ({
  folderName,
  idToken,
  dispatch,
  getState
}) => {
  const { code, res, errInfo } = await Restful.setBookmarkFolderAPI({
    idToken,
    data: { folderName: folderName.trim() }
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  const { needCreative, success, name, hash, list } = res;
  dispatch(setFolderList({ list }));

  return { needCreative, success, name, hash };
};

const changeFolderNameToBackend = async ({
  newFolderName,
  folderHash,
  idToken,
  dispatch,
  getState
}) => {
  const { code, res, errInfo } = await Restful.editBookmarkFolderAPI({
    idToken,
    data: { folderName: newFolderName.trim() },
    bookmarkFolderHash: folderHash
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  const { success, /*name, hash,*/ list } = res;
  dispatch(setFolderList({ list }));

  return { success };
};

const deleteFolderNameToBackend = async ({
  folderHash,
  idToken,
  dispatch,
  getState
}) => {
  const { code, res, errInfo } = await Restful.removeBookmarkFolderAPI({
    idToken,
    bookmarkFolderHash: folderHash
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }
  const { success, list } = res;
  dispatch(setFolderList({ list }));

  return { success };
};

export const initOutfitList = idToken => async (dispatch, getState) => {
  const { nowHash } = getNowFolder({ getState });
  if (!!nowHash) return null;

  await Promise.all([
    getFirstList({ hash: "all", idToken, dispatch, getState }),
    getFolderList({ idToken, dispatch, getState })
  ]);
};

export const getOutfitList = idToken => async (dispatch, getState) => {
  const bookmarkState = getBookmarkState({ getState });
  const { isEnd } = bookmarkState;
  const isLoading = getIsLoading({ getState });
  const { nowHash: hash } = getNowFolder({ getState });

  if (!hash) throw Error("null hash");
  if (isLoading || isEnd) return null;

  dispatch(setIsLoading(true));
  try {
    await addNewList({ hash, idToken, dispatch, getState });
  } catch (error) {
    dispatch(setIsLoading(false));
    throw error;
  }

  const { nowHash } = getNowFolder({ getState });
  if (nowHash === hash) {
    dispatch(exportBookmarkData(hash));
  }

  dispatch(setIsLoading(false));
};

export const changeOutfitListOfFolder =
  ({ idToken, folderHash }) =>
  async (dispatch, getState) => {
    await getFirstList({ hash: folderHash, idToken, dispatch, getState });
  };

export const refreshFolderList =
  ({ idToken }) =>
  async (dispatch, getState) => {
    getFolderList({ idToken, dispatch, getState });
  };

export const createBookmarkFolder =
  ({ folderName, idToken }) =>
  async (dispatch, getState) => {
    const { needCreative, success, name, hash } =
      await writeNewFolderNameToBackend({
        folderName,
        idToken,
        dispatch,
        getState
      });

    return { needCreative, success, name, hash };
  };

export const editBookmarkFolder =
  ({ folderHash, newFolderName, idToken }) =>
  async (dispatch, getState) => {
    const { success } = await changeFolderNameToBackend({
      newFolderName,
      folderHash,
      idToken,
      dispatch,
      getState
    });
    return { success };
  };

export const deleteBookmarkFolder =
  ({ folderHash, idToken }) =>
  async (dispatch, getState) => {
    const { success } = await deleteFolderNameToBackend({
      folderHash,
      idToken,
      dispatch,
      getState
    });
    return { success };
  };

export const removeOldSavedList =
  ({ hash }) =>
  (dispatch, getState) => {
    dispatch(initFolderObj(hash));
  };

export const removeExportedList = () => async (dispatch, getState) => {
  dispatch(setNowFolderHashAndValue({ hash: null, name: null }));
  dispatch(initExportedList());
};

export const deleteAllData = () => (dispatch, getState) => {
  dispatch(initAllData());
};

const initialState = fromJS({
  bookmarkObj: {},
  /*
  bookmarkObj 의 구조.
  bookmarkObj: {
    all: {
      savedList: [],
      expirationDate: null,
      isEnd: false
    },
    hash1: {
      savedList: [],
      expirationDate: null,
      isEnd: false
    }
    ...
  },
  */

  bookmarkFolderList: [{ hash: "all", name: "all" }],

  nowBookmarkFolderHash: null,
  nowBookmarkFolderName: null,

  outfitList: [],
  isEnd: false,
  isLoading: false
});

export default handleActions(
  {
    [SET_NOW_FOLDER_HASH_AND_VALUE]: (state, action) => {
      const { hash, name } = action.payload;
      return state
        .set("nowBookmarkFolderHash", fromJS(hash))
        .set("nowBookmarkFolderName", fromJS(name));
    },
    [SET_OUTFIT_LIST]: (state, action) => {
      const { hash, savedList } = action.payload;
      return state.setIn(["bookmarkObj", hash, "savedList"], fromJS(savedList));
    },
    [SET_FOLDER_LIST]: (state, action) => {
      const { list } = action.payload;
      const { bookmarkFolderList } = initialState.toJS();
      const newList = bookmarkFolderList.concat(list);
      return state.set("bookmarkFolderList", fromJS(newList));
    },
    [CHECK_IS_END]: (state, action) => {
      const { hash, length } = action.payload;
      const isEnd = length === UNIT_LENGTH ? false : true;
      return state.setIn(["bookmarkObj", hash, "isEnd"], fromJS(isEnd));
    },
    [SET_EXPIRATION_DATE]: (state, action) => {
      const { hash, expirationDate } = action.payload;
      return state.setIn(
        ["bookmarkObj", hash, "expirationDate"],
        fromJS(expirationDate)
      );
    },
    [SET_IS_LOADING]: (state, action) => {
      const isLoading = action.payload;
      return state.set("isLoading", fromJS(isLoading));
    },
    [EXPORT_BOOKMARK_DATA]: (state, action) => {
      const hash = action.payload;
      const { savedList, isEnd } = state.toJS().bookmarkObj[hash];
      return state
        .set("outfitList", fromJS(savedList))
        .set("isEnd", fromJS(isEnd));
    },
    [INIT_EXPORTED_LIST]: (state, action) => {
      const { outfitList, isEnd } = initialState.toJS();
      return state
        .set("outfitList", fromJS(outfitList))
        .set("isEnd", fromJS(isEnd));
    },
    [INIT_FOLDER_OBJ]: (state, action) => {
      const hash = action.payload;
      const initObj = {
        savedList: [],
        expirationDate: null,
        isEnd: false
      };
      return state.setIn(["bookmarkObj", hash], fromJS(initObj));
    },
    [INIT_ALL_DATA]: (state, action) => {
      return initialState;
    }
  },
  initialState
);
